import Logger from "slotworks/utils/logger";
import Timer from "slotworks/utils/timer";
import * as TweenJS from "slotworks/utils/tween";

declare const __DEBUG__: boolean;

export default class Contract<T> {

    public static empty(): Contract<any> {
        return new Contract<any>((resolve) => resolve(null));
    }

    public static never(): Contract<any> {
        return new Contract<any>((resolve) => {
            // never resolve
        });
    }

    public static wrap<T>(method?: () => T): Contract<any> {
        return new Contract<any>((resolve) => {
            let result = null;
            if (method) {
                result = method();
            }
            resolve(result);
        });
    }

    public static wrapTween(tween: TweenJS.Tween): Contract<any> {
        return new Contract<any>((resolve) => {

            let userOnComplete: (object?: any) => void;
            if (tween.getOnComplete()) {
                userOnComplete = tween.getOnComplete();
            }

            tween.onComplete(() => {
                if (userOnComplete) {
                    userOnComplete();
                }
                resolve(null);
            });

            tween.start();
        });
    }

    public static getTimeoutContract(duration: number): Contract<any> {
        return new Contract<number>((resolve) => {
            Timer.setTimeout(resolve, duration);
        });
    }

    public static getDelayedContract<T>(delay: number, callback: () => Contract<T>): Contract<T> {
        const delayedContract: Contract<T> = new Contract((resolve) => {
            if (delay) {
                Timer.setTimeout(() => {
                    if (!delayedContract.isCancelled()) {
                        callback().then(resolve);
                    }
                }, delay);
            } else {
                callback().then(resolve);
            }
        });

        return delayedContract;
    }

    private executor: (resolve: (value?: T) => void) => (void | Contract<any>);

    private timeoutId: number;
    private cancelled: boolean;
    private fulfilled: boolean;
    private onfulfilled: (value: T) => void;

    /**
     * A Contract is like a Promise however the results can be returned immediately (syncronously)
     * A Contract will ALWAYS be fulfilled, unless it is cancelled manually
     * A Contract is only executed when "then" or "execute" is called (and never both)
     * If "then" or "execute" aren't called on a contract by the next "tick", an exception is thrown
     */
    constructor(executor: (resolve: (value?: T) => void) => (void | Contract<any>)) {
        this.executor = executor;

        if (__DEBUG__) {
            const e = new Error();

            this.timeoutId = Timer.setTimeout(() => {
                throw new Error("Contract never executed " + e.stack);
            }, 1);
        }
    }

    /**
     * When a contract has resolved, call onfulfilled with the value passed to resolve
     */
    public then(onfulfilled: (value: T) => void) {
        if (__DEBUG__) {
            Timer.clearTimeout(this.timeoutId);
        }

        if (this.executor) {
            this.onfulfilled = onfulfilled;

            this.fulfilled = false;
            this.executor((value: T) => {
                if (this.cancelled) {
                    Logger.debug("Cancelled contract resolved");
                } else {
                    if (!this.fulfilled) {
                        this.fulfilled = true;
                    } else {
                        const e = new Error();
                        throw new Error("Contract resolved twice " + e.stack);
                    }
                    this.onfulfilled(value);
                    this.onfulfilled = null;
                }
            });
            this.executor = null;
        }

        return this;
    }

    /**
     * Execute the contract without anything waiting for resolve
     */
    public execute() {
        if (__DEBUG__) {
            Timer.clearTimeout(this.timeoutId);
        }

        if (this.executor) {
            this.executor(() => null);
            this.executor = null;
        }
    }

    /**
     * Cancel a contract. It can never be resolved
     */
    public cancel() {
        if (__DEBUG__) {
            Timer.clearTimeout(this.timeoutId);
        }

        this.cancelled = true;
        this.onfulfilled = null;
    }

    public isCancelled() {
        return this.cancelled;
    }

    /**
     * Force a contract to resolve
     * If the contract subsequently resolves itself, it will be ignored
     * If the contract has already been resolved, this call will be ignored
     * If the contract has not yet been executed, this call will be ignored
     */
    public forceResolve(value?: T) {
        if (!this.cancelled && !this.fulfilled && this.onfulfilled) {
            this.fulfilled = true;
            this.onfulfilled(value);
            this.onfulfilled = null;
        }
    }
}
